// Realistic smartphone frame rendering a generated Instagram Story preview,
// including text overlays, sticker previews (polls, questions, countdowns,
// links, product) and a CTA button.
import React from 'react';
import { Link2, Clock3, HelpCircle, ShoppingBag, ChevronUp } from 'lucide-react';
import { MTN_BRAND, MtnPost, StorySticker, pj } from '@/lib/mtnshop';


function Sticker({ s }: { s: StorySticker }) {
  const type = (s.type || '').toLowerCase();
  if (type === 'poll') {
    return (
      <div className="w-full max-w-[200px] rounded-2xl bg-white/95 p-2.5 text-center shadow-lg">
        <p className="text-[11px] font-bold text-zinc-900 mb-1.5">{s.label}</p>
        <div className="grid grid-cols-2 gap-1.5">
          <div className="rounded-lg bg-zinc-100 py-1 text-[11px] font-bold text-sky-600">YES</div>
          <div className="rounded-lg bg-zinc-100 py-1 text-[11px] font-bold text-pink-600">NO</div>
        </div>
      </div>
    );
  }
  if (type === 'question') {
    return (
      <div className="w-full max-w-[200px] rounded-2xl bg-white/95 p-2.5 text-center shadow-lg">
        <p className="flex items-center justify-center gap-1 text-[11px] font-bold text-zinc-900">
          <HelpCircle className="h-3 w-3" /> {s.label}
        </p>
        <div className="mt-1.5 rounded-lg bg-zinc-100 py-1.5 text-[10px] text-zinc-400">Type something…</div>
      </div>
    );
  }
  if (type === 'countdown') {
    return (
      <div className="rounded-2xl bg-gradient-to-r from-purple-600/90 to-pink-500/90 px-3.5 py-2 text-center shadow-lg">
        <p className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white/90">
          <Clock3 className="h-3 w-3" /> {s.label}
        </p>
        <p className="text-lg font-black text-white tracking-widest">02:14:36</p>
      </div>
    );
  }
  if (type === 'product') {
    return (
      <div className="flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 shadow-lg">
        <ShoppingBag className="h-3.5 w-3.5 text-zinc-900" />
        <p className="text-[11px] font-bold text-zinc-900">{s.label}</p>
      </div>
    );
  }
  // link / default
  return (
    <div className="flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 shadow-lg">
      <Link2 className="h-3.5 w-3.5 text-sky-600" />
      <p className="text-[11px] font-bold text-sky-700">{s.label || 'shopmtn.com'}</p>
    </div>
  );
}

export default function StoryPhoneFrame({ post }: { post: MtnPost }) {
  const stickers = pj<StorySticker[]>(post.story_stickers, []);
  return (
    <div className="relative mx-auto w-[240px]">
      {/* Phone body */}
      <div className="relative rounded-[36px] border-[6px] border-zinc-800 bg-zinc-900 shadow-[0_20px_60px_rgba(0,0,0,0.7)] overflow-hidden">
        {/* Dynamic island */}
        <div className="absolute left-1/2 top-2 z-30 h-[18px] w-[70px] -translate-x-1/2 rounded-full bg-black" />
        <div className="relative aspect-[9/19] w-full bg-zinc-950 overflow-hidden">
          {/* Background */}
          {post.image_url ? (
            <img src={post.image_url} alt="story" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-800 via-zinc-900 to-black" />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/60" />

          {/* Progress + header */}
          <div className="absolute inset-x-0 top-0 z-20 p-2.5 pt-7">
            <div className="flex gap-1 mb-2">
              <div className="h-[2px] flex-1 rounded bg-white" />
              <div className="h-[2px] flex-1 rounded bg-white/30" />
              <div className="h-[2px] flex-1 rounded bg-white/30" />
            </div>
            <div className="flex items-center gap-1.5">
              <img src={MTN_BRAND.avatar} alt="" className="h-6 w-6 rounded-full object-cover" />
              <p className="text-[10px] font-semibold text-white">{MTN_BRAND.handle}</p>
              <p className="text-[9px] text-white/60">2h</p>
            </div>
          </div>

          {/* Content overlays */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-4 pt-10">
            {post.story_text && (
              <p className="text-center text-[15px] font-black uppercase leading-tight text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                {post.story_text}
              </p>
            )}
            {stickers.slice(0, 2).map((s, i) => <Sticker key={i} s={s} />)}
          </div>

          {/* CTA */}
          <div className="absolute inset-x-0 bottom-3 z-20 flex flex-col items-center gap-0.5 px-4">
            <ChevronUp className="h-4 w-4 text-white animate-bounce" />
            <div className="w-full rounded-full bg-white py-1.5 text-center text-[11px] font-bold text-black">
              {post.cta || 'Shop now'}
            </div>
          </div>
        </div>
      </div>
      {/* Side buttons */}
      <div className="absolute -right-[3px] top-20 h-12 w-[3px] rounded-r bg-zinc-700" />
      <div className="absolute -left-[3px] top-16 h-8 w-[3px] rounded-l bg-zinc-700" />
      <div className="absolute -left-[3px] top-28 h-8 w-[3px] rounded-l bg-zinc-700" />
    </div>
  );
}
